/**
 * Profile Page Hook
 * Optimized hook for ProfilePage that uses store data instead of API calls
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@finitless/shared/lib";
import { apiClient } from "@finitless/shared/lib";
import { useUserStore } from "@finitless/shared/stores";

/**
 * Hook to update user profile
 */
const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const updateUserInStore = useUserStore((state) => state.updateUser);

  return useMutation({
    mutationKey: ["user", "updateProfile"],
    mutationFn: async (profileData) => {
      const userId = profileData.userId;
      if (!userId) {
        throw new Error("User ID is required");
      }

      const response = await apiClient.post(`/users/${userId}`, profileData);

      if (response.error) {
        const errorMessage = Array.isArray(response.result)
          ? response.result.join(", ")
          : response.result || "Profile update failed";
        throw new Error(errorMessage);
      }

      return response.result || response;
    },
    onSuccess: (updatedUser) => {
      updateUserInStore(updatedUser);
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() });
    },
    onError: (error) => {
      console.error("Profile update failed:", error);
    },
  });
};

/**
 * Hook for ProfilePage that uses store data directly
 * No API calls needed since user data is already in the store
 */
export const useProfilePage = () => {
  const user = useUserStore((state) => state.user);
  const updateProfileMutation = useUpdateProfile();

  return {
    user,
    isLoading: false,
    error: null,
    updateProfile: updateProfileMutation.mutateAsync,
    isUpdating: updateProfileMutation.isPending,
    updateError: updateProfileMutation.error,
  };
};
